import React from "react";
import { Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import MainLayout from "components/layout/MainLayout"; // Import MainLayout

import { useSelector } from "react-redux";

// Pages
import NotFound from "pages/NotFound";
import SearchResults from './pages/search-results';
import LifecycleDashboard from './pages/lifecycle-dashboard';
import AssetListPage from './pages/asset-list';
import AssetDetails from './pages/asset-details';
import AssetRegistration from './pages/asset-registration';
import Dashboard from './pages/dashboard';
import Login from './pages/login';
import CheckoutManagement from './pages/checkout-management';
import SupplierManagement from './pages/supplier-management';
import UserRegistration from 'pages/admin/UserRegistration';
import EmployeeManagement from 'pages/admin/EmployeeManagement';
import DepartmentManagement from 'pages/admin/DepartmentManagement';
import LocationsPage from 'pages/admin/LocationManagement'; // Renamed to LocationsPage as per requirement
import AuditReports from 'pages/admin/AuditReports';
import AllActivities from 'pages/AllActivities'; // Import AllActivities
import BulkImportTemplate from 'pages/BulkImportTemplate'; // Import BulkImportTemplate
import MfaSetup from 'pages/MfaSetup';
import WriteOffsPage from 'pages/write-offs';
import SoftwareDashboard from 'pages/software';
import SoftwareDetail from 'pages/software/SoftwareDetail';
import ScannerPage from 'pages/scanner';
import DepartmentAuditPage from 'pages/department-audit';
import ProfileSettings from 'pages/profile-settings';
import Preferences from 'pages/preferences';

const Routes = () => {
  React.useEffect(() => {
    console.log("✅ ROUTES COMPONENT MOUNTED");
    return () => console.log("❌ ROUTES COMPONENT UNMOUNTED");
  }, []);

  const { loading } = useSelector((state) => state.auth);
  console.log("ROUTER RENDER: Current loading state is", loading);

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* --- Public Routes --- */}
        <Route path="/login" element={<Login />} />

        {/* --- Protected Routes --- */}
        {/* All routes inside this wrapper require login and get the MainLayout */}
        <Route path="/scanner" element={<ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'pic', 'department_pic', 'manager', 'user']}><ScannerPage /></ProtectedRoute>} />
        
        <Route 
          element={
            <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'pic', 'department_pic', 'manager', 'user']}>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile-settings" element={<ProfileSettings />} />
          <Route path="/preferences" element={<Preferences />} />
          
          <Route 
            path="/checkout-management" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <CheckoutManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/supplier-management" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <SupplierManagement />
              </ProtectedRoute>
            } 
          />
          <Route path="/search-results" element={<SearchResults />} />
          <Route 
            path="/lifecycle-planning" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <LifecycleDashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="/asset-list" element={<AssetListPage />} />
          <Route path="/assets/:asset_tag" element={<AssetDetails />} />
          <Route 
            path="/asset-registration" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <AssetRegistration />
              </ProtectedRoute>
            } 
          />
          <Route path="/bulk-import-template" element={<BulkImportTemplate />} /> {/* New route for BulkImportTemplate */}
          <Route path="/all-activities" element={<AllActivities />} /> {/* New route for AllActivities */}
          <Route 
            path="/write-offs" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <WriteOffsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/software" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <SoftwareDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/software/:id" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <SoftwareDetail />
              </ProtectedRoute>
            } 
          />
          <Route path="/mfa-setup" element={<MfaSetup />} />
          <Route path="/department-audit" element={<DepartmentAuditPage />} />

          {/* Admin Routes */}
          <Route 
            path="/admin/user-registration" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin']}>
                <UserRegistration />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/employee-management" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager', 'pic', 'department_pic']}>
                <EmployeeManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/department-management" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <DepartmentManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/location-management" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager']}>
                <LocationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/audit-reports" 
            element={
              <ProtectedRoute allowedRoles={['system_admin', 'admin', 'it_staff', 'manager', 'pic', 'department_pic']}>
                <AuditReports />
              </ProtectedRoute>
            } 
          />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </ErrorBoundary>
  );
};

export default Routes;